/**
 * clientHealth — "is THIS window's Firestore client still alive?"
 *
 * WHY (Walter 2026-09-06). His PWA window sat for 22 hours showing a green
 * "synced" check while displaying content a day out of date. The sync log
 * named the moment it died:
 *
 *   20:00:16  save-failed    w=bjb91/pwa  msg=FIRESTORE (10.14.1) INTERNAL
 *                            ASSERTION FAILED: Unexpected state  online=true
 *   20:00:19  saved-via-api  w=bjb91/pwa
 *   20:05:55  save-failed    w=bjb91/pwa  (the same assertion)
 *   20:05:56  saved-via-api  w=bjb91/pwa
 *   … and that window never appears in the log again.
 *
 * `INTERNAL ASSERTION FAILED` is a panic inside the Firebase SDK's own async
 * queue. Once it fires, that page's Firestore client is finished: listeners
 * stop delivering and direct writes keep failing. It does not recover, and
 * the disableNetwork/enableNetwork bounce in useFirestoreSyncMonitor cannot
 * revive it — only constructing a new client can, which in practice means
 * reloading the page.
 *
 * TWO THINGS WE BUILT HID IT, and both were individually correct:
 *
 *   1. The /api/saveList fallback (2026-08-20) kept his edits landing over
 *      our own domain, so the app had no failing writes left to report.
 *   2. readHealth watches for `metadata.fromCache === true` — the signature
 *      of a BLOCKED channel, where snapshots keep arriving but from cache.
 *      A CRASHED client emits nothing at all, so fromCache never flips,
 *      `isStale` stays false, and both the "reconnecting" caption and the
 *      REST read fallback stay switched off. We had a detector for the
 *      wrong failure mode.
 *
 * So this module watches for the death itself, from two directions: the
 * assertion text wherever it surfaces (a rejected write, an uncaught error,
 * an unhandled rejection), and — via markClientDead from the listener
 * watchdog in useData — the symptom, which is the server being demonstrably
 * ahead of a listener that claims to be connected. The second one is the
 * important half: it catches a deaf window whatever killed it, including
 * causes we haven't seen yet.
 *
 * Deliberately a ONE-WAY LATCH. There is no recovery short of a reload, so
 * a window that has been marked dead stays marked; anything else would flap
 * a banner that the user cannot act on twice.
 */

import { logSync } from './syncLog';

// The SDK's own panic message. Matching the text is unpleasant but it is the
// only signal the SDK gives us — it throws a plain Error with no code.
const ASSERTION_RE = /INTERNAL ASSERTION FAILED/i;

let dead = false;
let reason = null;
let since = null;
let installed = false;
const listeners = new Set();

function emit() {
  for (const fn of listeners) {
    try { fn(dead); } catch { /* a listener must never break the app */ }
  }
}

export function isClientDead() { return dead; }

export function getClientHealth() { return { dead, reason, since }; }

export function subscribeClientHealth(fn) {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/** Does this error carry the SDK's fatal assertion? */
export function looksFatal(err) {
  const msg = typeof err === 'string' ? err : String(err?.message || '');
  return ASSERTION_RE.test(msg);
}

/**
 * Latch this window as dead. `reason` is for the log and the banner's
 * tooltip; the user-facing text is the same either way, because the remedy
 * is the same either way.
 */
export function markClientDead(nextReason, detail = {}) {
  if (dead) return false;
  dead = true;
  reason = nextReason;
  since = Date.now();
  try { logSync('client-dead', { reason: nextReason, ...detail }); } catch { /* never throw from a diagnostic */ }
  emit();
  return true;
}

/**
 * Feed an error here from any catch that wraps a Firestore call. Returns
 * true if it was the fatal one, so callers can log it differently — but the
 * caller's own fallback path should carry on regardless: a dead client still
 * saves fine over /api/saveList, and taking that away would turn a stale
 * window into a lossy one.
 */
export function noteClientError(err) {
  if (!looksFatal(err)) return false;
  markClientDead('sdk-assertion', {
    msg: String(err?.message || '').slice(0, 120).replace(/\s+/g, ' '),
  });
  return true;
}

/**
 * Catch the assertion when it surfaces OUTSIDE one of our try/catches —
 * which is the common case, since the SDK's queue panics on its own timer
 * rather than inside a call we made. Idempotent; safe to call from a mount
 * effect that may run twice under StrictMode.
 */
export function installClientErrorWatch() {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  window.addEventListener('error', (e) => {
    if (looksFatal(e?.error) || looksFatal(e?.message)) {
      markClientDead('sdk-assertion-uncaught');
    }
  });
  window.addEventListener('unhandledrejection', (e) => {
    if (looksFatal(e?.reason)) {
      markClientDead('sdk-assertion-rejection');
    }
  });
}

/** Test seam — resets module state between cases. */
export function __resetClientHealth() {
  dead = false;
  reason = null;
  since = null;
  installed = false;
  listeners.clear();
}
